import { useState, useEffect, useCallback, useContext } from 'react';
import axios from 'axios';
import { AppContext } from '../App';

/**
 * Hook for data-freshness-aware caching.
 * 
 * Checks /api/data/last-updated before using cached data.
 * If the DB hasn't changed since we last fetched, serves from sessionStorage instantly.
 * 
 * Usage:
 *   const { data, loading } = useCachedData('dashboard', fetchFn, [dep1, dep2]);
 */
export function useCachedData(cachePrefix, fetchFn, deps = []) {
  const { API } = useContext(AppContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cacheKey = `${cachePrefix}_${deps.map(d => d ?? '').join('_')}`;
  const metaKey = `${cacheKey}__meta`;

  const fetchFresh = useCallback(async (dbTimestamp) => {
    setLoading(true);
    try {
      const result = await fetchFn();
      setData(result);

      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(result));
        sessionStorage.setItem(metaKey, JSON.stringify({
          db_last_updated: dbTimestamp,
          cached_at: Date.now()
        }));
      } catch (storageErr) {
        console.warn(`Cache write failed for ${cachePrefix}:`, storageErr.message);
      }
    } catch (err) {
      console.error(`Failed to fetch data for ${cachePrefix}:`, err);
    } finally {
      setLoading(false);
    }
  }, [cacheKey, metaKey, cachePrefix, fetchFn]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      let dbTimestamp = null;
      try {
        const res = await axios.get(`${API}/data/last-updated`);
        dbTimestamp = res.data.last_updated;
      } catch (freshErr) {
        console.warn('Data freshness check failed, fetching fresh:', freshErr.message);
        await fetchFresh(null);
        return;
      }

      if (cancelled) return;

      try {
        const cachedRaw = sessionStorage.getItem(cacheKey);
        const metaRaw = sessionStorage.getItem(metaKey);
        if (cachedRaw && metaRaw) {
          const meta = JSON.parse(metaRaw);
          if (meta.db_last_updated === dbTimestamp) {
            setData(JSON.parse(cachedRaw));
            setLoading(false);
            return;
          }
        }
      } catch (cacheErr) {
        console.warn('Cache read failed, fetching fresh:', cacheErr.message);
      }

      if (cancelled) return;

      await fetchFresh(dbTimestamp);
    };

    run();
    return () => { cancelled = true; };
  }, [API, cacheKey, metaKey, fetchFresh]);

  return { data, loading };
}
